<template>
    <div class="enter-contact-info-button" @click="onClick">
        <div class="contact-info-icon-left">
            <i class="fa-regular fa-user fa-xl"></i>
        </div>

        <div v-if="isNoContactInfoProvided" class="no-contact-info-container">
            <div class="text-enter-contact-data">{{ translations.enterContactDataPopover.title }}</div>
        </div>
        <div v-else class="contact-info-container">
            <div v-if="fullName.length !== 0" class="contact-full-name">{{ fullName }}</div>
            <div v-if="email.length !== 0" class="contact-email">{{ email }}</div>
            <div v-if="phoneNumber.length !== 0" class="contact-phone-number">{{ phoneNumber }}</div>

            <div v-if="isCompletedContactInfoProvided == false" class="text-complete-contact-data">
                {{ translations.enterContactDataPopover.completeOrderData }}
            </div>
        </div>

        <div class="contact-info-icon-right">
            <i class="fal fa-arrow-right fa-xl"></i>
        </div>
    </div>
    <div class="line" style="height: 1px; width: 100%; background-color: #f5f5f5"></div>
</template>

<script setup lang="ts">
    import { EnterContactInfoContentPopover } from "@/app/popover/EnterContactInfoContentPopover";
    import { Popup } from "@/components/dialog/Popup";
    import type { IContactData } from "@/data/model/Profile";
    import { useCartStore } from "@/data/stores/cart";
    import { useIdentityStore } from "@/data/stores/identity";
    import { translations as trans, translations } from "@/shared/localization/translations";
    import { computed, onMounted } from "vue";

    import EnterContactDataForm from "@/components/pay/EnterContactDataForm.vue";

    const cart = useCartStore();
    const identity = useIdentityStore();

    const props = defineProps<{
        isInOperatorApp: boolean;
        onClickOpenProfileView?: () => void;
    }>();

    const isNoContactInfoProvided = computed(() => {
        return fullName.value.length === 0 && email.value.length === 0 && phoneNumber.value.length === 0;
    });

    const isCompletedContactInfoProvided = computed<boolean>(() => {
        let ordererRequirements = cart.orderService!.useCase!.ordererRequirements!;

        // Check first name
        if (
            ordererRequirements.isFirstNameRequired &&
            (cart.currentCart?.contactDataEndUser?.firstName ?? "").length == 0
        ) {
            console.log("Add first name");
            return false;
        }

        // Check last name
        if (
            ordererRequirements.isLastNameRequired &&
            (cart.currentCart?.contactDataEndUser?.lastName ?? "").length == 0
        ) {
            console.log("Add last name");
            return false;
        }

        // Check email
        if (ordererRequirements.isEmailRequired && (cart.currentCart?.contactDataEndUser?.email ?? "").length == 0) {
            console.log("Add email");
            return false;
        }

        // Check phone number
        if (
            ordererRequirements.isPhoneNumberRequired &&
            (cart.currentCart?.contactDataEndUser?.phoneNumber ?? "").length == 0
        ) {
            console.log("Add phone");
            return false;
        }

        return true;
    });

    const fullName = computed<string>(() => {
        return `${cart.currentCart?.contactDataEndUser?.firstName ?? ""} ${cart.currentCart?.contactDataEndUser?.lastName ?? ""}`.trim();
    });

    const email = computed<string>(() => {
        return cart.currentCart?.contactDataEndUser?.email ?? "";
    });

    const phoneNumber = computed<string>(() => {
        return cart.currentCart?.contactDataEndUser?.phoneNumber ?? "";
    });

    onMounted(() => {});

    function onClick() {
        if (props.isInOperatorApp) {
            let popup = Popup.show({
                component: EnterContactDataForm,
                componentProps: {
                    isInOperatorApp: props.isInOperatorApp,
                    hide: () => {
                        popup?.close();
                    },
                },
                showConfirmButton: false,
                closePopupOnOverlayClick: true,
                showCloseButton: true,
                title: trans.enterContactDataPopover.title,
            });
        } else {
            if (identity.hasOperatorAccess || !identity.isAuthenticated || identity.identityId !== cart.currentCart?.contactDataEndUser?.identityId) {
                const popover = new EnterContactInfoContentPopover(props.isInOperatorApp);
                popover.show();
            } else {
                if (props.onClickOpenProfileView != null) {
                    props.onClickOpenProfileView();
                }
            }
        }
    }

    function getContactData(): IContactData | undefined {
        return cart.currentCart?.contactDataEndUser;
    }

    defineExpose({
        getContactData,
    });
</script>

<style scoped lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";

    .enter-contact-info-button {
        display: flex;
        padding: 10px 24px;
        justify-content: space-between;
        min-height: 40px;
        align-items: center;

        .contact-info-icon-left {
            color: colors.$lightFontColor;
        }

        > .no-contact-info-container {
            > .text-enter-contact-data {
                color: colors.$red;
                margin: 0 24px;
                font-weight: 600;
                width: 100%;
            }
        }

        > .contact-info-container {
            width: 100%;
            margin: 0 24px;

            .contact-full-name {
                font-size: 12px;
                font-weight: 700;
            }

            .contact-email {
                font-size: 12px;
            }

            .contact-phone-number {
                font-size: 12px;
            }

            .complete-contact-data-btn {
                font-size: 12px;
                font-weight: 700;
            }

            .text-complete-contact-data {
                color: colors.$pink;
                font-weight: 700;
                font-size: 12px;
                width: 100%;
            }
        }

        .contact-info-icon-right {
            color: colors.$lightFontColor;
        }
    }
</style>
